import { apiFetch } from './apiFetch';
import type {
  CreateCreditAccountCardRequest,
  CreateCreditDepositRequest,
  CreditAccountCard,
  CreditDepositReceipt,
  CreditDepositSummary,
  CreditGrouping,
  CreditPayment,
  CreditSettings,
  ListCreditDepositsQuery,
  ListCreditPendingQuery,
  UpdateCreditIssuerRequest,
  UpsertCreditAcquirerRequest,
  UpsertCreditBankRequest,
} from '@deposits/shared';

export async function fetchCreditSettings(locationId: number): Promise<CreditSettings> {
  return apiFetch<CreditSettings>(`/credit/settings?location_id=${locationId}`);
}

export async function updateCreditGrouping(locationId: number, grouping: CreditGrouping): Promise<CreditSettings> {
  return apiFetch('/credit/settings/grouping', {
    method: 'PUT',
    body: JSON.stringify({ location_id: locationId, grouping }),
  });
}

export async function updateCreditIssuer(body: UpdateCreditIssuerRequest): Promise<CreditSettings> {
  return apiFetch('/credit/settings/issuers', { method: 'PUT', body: JSON.stringify(body) });
}

export async function fetchCreditAccountCards(locationId: number): Promise<CreditAccountCard[]> {
  return apiFetch<CreditAccountCard[]>(`/credit/account-cards?location_id=${locationId}`);
}

export async function createCreditAccountCard(body: CreateCreditAccountCardRequest): Promise<CreditAccountCard> {
  return apiFetch('/credit/account-cards', { method: 'POST', body: JSON.stringify(body) });
}

export type CreditEntity = 'banks' | 'acquirers';

export async function saveCreditEntity(
  entity: CreditEntity,
  id: number | undefined,
  body: UpsertCreditBankRequest | UpsertCreditAcquirerRequest,
): Promise<CreditSettings> {
  return apiFetch(`/credit/settings/${entity}${id != null ? `/${id}` : ''}`, {
    method: id != null ? 'PUT' : 'POST',
    body: JSON.stringify(body),
  });
}

export async function deleteCreditEntity(
  entity: CreditEntity,
  id: number,
  locationId: number,
): Promise<CreditSettings> {
  return apiFetch(`/credit/settings/${entity}/${id}?location_id=${locationId}`, { method: 'DELETE' });
}

export async function fetchCreditPending(q: ListCreditPendingQuery): Promise<CreditPayment[]> {
  const qs = new URLSearchParams({
    dateField: q.dateField,
    from: q.from,
    to: q.to,
    location_id: String(q.location_id),
  });
  if (q.project_id != null) qs.set('project_id', String(q.project_id));
  return apiFetch<CreditPayment[]>(`/credit/pending?${qs.toString()}`);
}

export async function fetchCreditDeposits(q: ListCreditDepositsQuery): Promise<CreditDepositSummary[]> {
  const qs = new URLSearchParams({ location_id: String(q.location_id), from: q.from, to: q.to });
  if (q.project_id != null) qs.set('project_id', String(q.project_id));
  return apiFetch<CreditDepositSummary[]>(`/credit/deposits?${qs.toString()}`);
}

export async function fetchCreditDeposit(
  id: number,
): Promise<{ deposit: CreditDepositSummary; receipts: CreditDepositReceipt[] }> {
  return apiFetch(`/credit/deposits/${id}`);
}

export interface CreatedCreditDeposit {
  id: number;
  deposit_number: number;
  pdf_status: 'ready' | 'failed';
}

export async function createCreditDeposit(body: CreateCreditDepositRequest): Promise<CreatedCreditDeposit> {
  return apiFetch('/credit/deposits', { method: 'POST', body: JSON.stringify(body) });
}

export async function cancelCreditDeposit(id: number, reason?: string): Promise<void> {
  await apiFetch<void>(`/credit/deposits/${id}/cancel`, {
    method: 'POST',
    body: JSON.stringify(reason ? { reason } : {}),
  });
}
